<template>
    <div class="p-6 sm:ml-64 mt-16">
        <!-- Statistik Cards -->
        <div class="grid grid-cols-4 gap-6 mb-6">
            <div class="flex flex-col items-center justify-center p-6 rounded-lg bg-white shadow">
                <i class="fas fa-users text-4xl text-blue-500"></i>
                <p class="text-gray-700 mt-2">Total Users</p>
                <h2 class="text-2xl font-bold">{{ totalUsers }}</h2>
            </div>
            <div class="flex flex-col items-center justify-center p-6 rounded-lg bg-white shadow">
                <i class="fas fa-user-check text-4xl text-green-500"></i>
                <p class="text-gray-700 mt-2">Active Users</p>
                <h2 class="text-2xl font-bold">{{ totalActiveUsers }}</h2>
            </div>
            <div class="flex flex-col items-center justify-center p-6 rounded-lg bg-white shadow">
                <i class="fas fa-user-clock text-4xl text-yellow-500"></i>
                <p class="text-gray-700 mt-2">Menunggu Approval</p>
                <h2 class="text-2xl font-bold">{{ totalPendingUsers }}</h2>
            </div>
            <div class="flex flex-col items-center justify-center p-6 rounded-lg bg-white shadow">
                <i class="fas fa-money-bill-wave text-4xl text-red-500"></i>
                <p class="text-gray-700 mt-2">Total Transaksi</p>
                <h2 class="text-2xl font-bold">{{ totalTransactions }}</h2>
            </div>
        </div>

        <!-- Baris kedua (2 items) -->
        <div class="grid grid-cols-2 gap-6">
            <div class="flex flex-col items-center justify-center p-6 rounded-lg bg-white shadow">
                <i class="fas fa-money-bill-wave text-4xl text-red-500"></i>
                <p class="text-gray-700 mt-2">Total Rp Transaksi</p>
                <h2 class="text-2xl font-bold">{{ formatCurrency(totalAmountTransactions) }}</h2>
            </div>
            <div class="flex flex-col items-center justify-center p-6 rounded-lg bg-white shadow">
                <i class="fas fa-coins text-4xl text-green-500"></i>
                <p class="text-gray-700 mt-2">Total Rp Admin</p>
                <h2 class="text-2xl font-bold">{{ formatCurrency(totalAmountAdmin) }}</h2>
            </div>
        </div>

        <!-- Tabel Transaksi Hari Ini -->
        <div class="bg-white p-6 shadow-lg rounded-lg mt-6">
            <h2 class="text-xl font-bold mb-4 text-gray-700">📅 Transaksi Hari Ini</h2>
            <table class="w-full text-sm text-left text-gray-700 border-collapse">
                <thead class="bg-gray-200 text-gray-600 uppercase">
                    <tr>
                        <th class="px-4 py-2">Tanggal</th>
                        <th class="px-4 py-2">ID PEL</th>
                        <th class="px-4 py-2">Nama</th>
                        <th class="px-4 py-2">Total Bayar</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="transaction in todayTransactions" :key="transaction.id"
                        class="border-b hover:bg-gray-100 transition">
                        <td class="px-4 py-2">{{ formatDate(transaction.timestamp) }}</td>
                        <td class="px-4 py-2">{{ transaction['IDPEL'] }}</td>
                        <td class="px-4 py-2">{{ transaction['NAMA'] }}</td>
                        <td class="px-4 py-2 text-green-600 font-bold">{{ formatCurrency(parseNominal(
                            transaction['TOTAL BAYAR'] ??
                            transaction['RP BAYAR'] ??
                            transaction['RP TAG PLN'] ??
                            "0"
                        )) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { getFirestore, collection, getDocs, query, where, Timestamp } from "firebase/firestore";

// Firestore instance
const db = getFirestore();

// Data references
const totalUsers = ref(0);
const totalActiveUsers = ref(0);
const totalPendingUsers = ref(0);
const totalTransactions = ref(0);
const totalAmountTransactions = ref(0);
const totalAmountAdmin = ref(0);
const todayTransactions = ref([]);

// Fetch data from Firestore
const fetchData = async () => {
    try {
        // Fetch Total Users
        const usersSnapshot = await getDocs(collection(db, "users"));
        const usersData = usersSnapshot.docs.map(doc => doc.data());

        totalUsers.value = usersData.length;
        totalActiveUsers.value = usersData.filter(user => user.status === "active").length;
        totalPendingUsers.value = usersData.filter(user => user.status === "inactive").length;

        // Fetch Total Transaksi
        const transactionsSnapshot = await getDocs(collection(db, "bills"));
        totalTransactions.value = transactionsSnapshot.size;

        // Fetch Total Rp Transaksi
        await fetchTotalAmountTransactions();
        await fetchTotalAdmin(); // Hitung total admin

        // Fetch Transaksi Hari Ini
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);

        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);

        const todayQuery = query(
            collection(db, "bills"),
            where("timestamp", ">=", Timestamp.fromDate(todayStart)),
            where("timestamp", "<=", Timestamp.fromDate(todayEnd))
        );

        const todaySnapshot = await getDocs(todayQuery);
        todayTransactions.value = todaySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    } catch (error) {
        console.error("Error fetching data:", error);
    }
};

// Fungsi untuk mengambil dan menghitung total "Admin Bank"
const fetchTotalAdmin = async () => {
    try {
        let totalAdmin = 0;
        const transactionsSnapshot = await getDocs(collection(db, "bills"));

        transactionsSnapshot.forEach((doc) => {
            const billData = doc.data();
            if (billData["Admin Bank"]) {
                totalAdmin += parseNominal(billData["Admin Bank"]);
            }
        });

        totalAmountAdmin.value = totalAdmin;
    } catch (error) {
        console.error("Error fetching total admin:", error);
    }
};

// Fetch Total Rp Transaksi
const fetchTotalAmountTransactions = async () => {
    try {
        let total = 0;
        const billsSnapshot = await getDocs(collection(db, "bills"));

        billsSnapshot.forEach((billDoc) => {
            const billData = billDoc.data();
            let nominal = "0";

            if (billData["RP BAYAR"]) {
                nominal = billData["RP BAYAR"].toString();
            } else if (billData["RP TAG PLN"]) {
                nominal = billData["RP TAG PLN"].toString();
            } else if (billData["TOTAL BAYAR"]) {
                nominal = billData["TOTAL BAYAR"].toString();
            }

            total += parseNominal(nominal);
        });

        totalAmountTransactions.value = total;
    } catch (error) {
        console.error("Error fetching total amount transactions:", error);
    }
};

// Fungsi untuk mengonversi nominal ke angka
const parseNominal = (value) => {
    return value ? Number(value.replace(/[^\d]/g, "")) : 0;
};

// Fungsi format Rupiah
const formatCurrency = (value) => {
    return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR" }).format(value);
};

// Format tanggal
const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = timestamp.toDate();
    return date.toLocaleDateString("id-ID", { year: "numeric", month: "long", day: "numeric" });
};

// Fetch data on mount
onMounted(fetchData);
</script>
